import React, { useState, useEffect, useRef, useContext } from "react";
import MessageList from "./MessageList";
import MessageInput from "../messageInput";
import ChatHeader from "./header/ChatHeader";
import { ChatBarDrawer } from "./sidebar/ChatBarDrawer";
import { Button, Checkbox, Label, Modal, TextInput } from "flowbite-react";
import type { ChatRoom, MessageData } from "@/types/chat.types";
import { useMessageOperations } from "@/hooks/useMessageOperations";
import { useLazyGetChatRoomMessagesQuery } from "@/api/chatApi/chat";
import { toast } from "react-toastify";
import { Agent } from "../../globals/availableAgents";
import { ChatContext, ChatContextInterface } from "../MSRContext";
import generateRandomString from "../../MSRP/utilities/generators";
import Global from "../../globals/globals";

/**
 * ChatAreaChild Component
 *
 * This is a CHILD component that displays chat for a selected room.
 * It receives the selected room from parent (ConversationsSidebarContainer)
 * and handles all message operations for that room.
 *
 * Parent: ConversationsSidebarContainer
 * Children: MessageList, MessageInput
 *
 * Props:
 * - room: The selected ChatRoom (from parent)
 * - wrapper: MSRP wrapper instance (from app root)
 * - onEditMessage: Callback for editing messages
 * - onDeleteMessage: Callback for deleting messages
 * - onSendMessage: Callback for sending messages
 */

interface ChatAreaChildProps {
  room: ChatRoom | null;
  wrapper: any;
  currentUser: string;
  onEditMessage?: (messageId: string, newText: string) => void;
  onDeleteMessage?: (messageId: string, agentUsername?: string) => void;
  onSendMessage?: (text: string, sender: string, isOwner: boolean, id: string) => void;
  onSendFileMessage?: (fileMessage: any) => void;
  onRefreshRooms?: () => void; // Callback to refresh the chat rooms list
  onClearSelection?: () => void; // Callback to clear the selected room
  onAutoSelectRoom?: (sessionKey: string) => void; // Callback to auto-select a room by session_key
}

const ChatAreaChild: React.FC<ChatAreaChildProps> = ({
  room,
  wrapper,
  currentUser,
  onEditMessage,
  onDeleteMessage,
  onSendMessage,
  onSendFileMessage,
  onRefreshRooms,
  onClearSelection,
  onAutoSelectRoom,
}) => {
  const [messages, setMessages] = useState<MessageData[]>([]);
  const [repliedMessage, setRepliedMessage] = useState<any>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 768);

  // Modal states
  const [isCloseSessionModalOpen, setIsCloseSessionModalOpen] = useState(false);
  const [closeSessionReason, setCloseSessionReason] = useState("");
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [selectedAgents, setSelectedAgents] = useState<Agent[]>([]);
  const [showConferenceNameInput, setShowConferenceNameInput] = useState(false);
  const [conferenceName, setConferenceName] = useState("");
  const [leaveConfirmationWindow, setLeaveConfirmationWindow] = useState(false);
  const [destroyConfirmationWindow, setDestroyConfirmationWindow] = useState(false);

  const PAGE_SIZE = 20;
  const notificationPermissionRef = useRef<NotificationPermission>("default");
  const [unreadCount, setUnreadCount] = useState(0);
  const originalTitleRef = useRef<string>(document.title);

  // Get agents and updateAgentList from context
  const { agents, updateAgentList } = useContext(ChatContext) as ChatContextInterface;

  // Fetch agent list on component mount (similar to agentList.tsx)
  useEffect(() => {
    console.log("ChatAreaChild mounted, fetching agent list...");
    updateAgentList("all-status");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array = run once on mount

  // Request notification permission on mount
  useEffect(() => {
    if ("Notification" in window) {
      notificationPermissionRef.current = Notification.permission;

      if (Notification.permission === "default") {
        Notification.requestPermission().then(permission => {
          notificationPermissionRef.current = permission;
          console.log("📬 Notification permission:", permission);
        });
      }
    }

    // Save original title
    originalTitleRef.current = document.title;

    return () => {
      // Restore original title on unmount
      document.title = originalTitleRef.current;
    };
  }, []);

  // Update browser tab title with unread count
  useEffect(() => {
    if (unreadCount > 0 && !document.hasFocus()) {
      document.title = `(${unreadCount}) ${originalTitleRef.current}`;
    } else {
      document.title = originalTitleRef.current;
    }
  }, [unreadCount]);

  // Reset unread count when window gains focus
  useEffect(() => {
    const handleFocus = () => {
      setUnreadCount(0);
      document.title = originalTitleRef.current;
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []);

  // Handle window resize for responsive layout
  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Play notification sound using Web Audio API
  const playNotificationSound = () => {
    try {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();

      // Create oscillator for the beep sound
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      // Configure the sound
      oscillator.frequency.value = 800; // Frequency in Hz (higher = higher pitch)
      oscillator.type = 'sine'; // Sine wave for smooth sound

      // Set volume (fade in and out)
      gainNode.gain.setValueAtTime(0, audioContext.currentTime);
      gainNode.gain.linearRampToValueAtTime(0.3, audioContext.currentTime + 0.01);
      gainNode.gain.linearRampToValueAtTime(0, audioContext.currentTime + 0.15);

      // Play the sound
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.15);
    } catch (err) {
      console.log('Could not play notification sound:', err);
    }
  };

  // Show browser notification for new messages
  const showNotification = (title: string, body: string, icon?: string) => {
    // Only show notification if window is not focused
    if (!document.hasFocus()) {
      // Increment unread count
      setUnreadCount(prev => prev + 1);

      // Play notification sound
      playNotificationSound();

      // Show browser notification if supported and permission granted
      if ("Notification" in window && notificationPermissionRef.current === "granted") {
        const notification = new Notification(title, {
          body: body,
          icon: icon || "/logo.png",
          badge: "/logo.png",
          tag: `message-${Date.now()}`,
          requireInteraction: false,
        });

        // Focus window when notification is clicked
        notification.onclick = () => {
          window.focus();
          setUnreadCount(0);
          notification.close();
        };

        // Auto-close after 5 seconds
        setTimeout(() => {
          notification.close();
        }, 5000);
      }
    }
  };

  // Fetch messages for selected room
  const [triggerMessages, { data: messagesData, isLoading: messagesLoading }] =
    useLazyGetChatRoomMessagesQuery();

  // Load messages when room changes
  useEffect(() => {
    if (room?.session_key) {
      setMessages([]);
      triggerMessages({ sessionKey: room.session_key, page: 1, limit: PAGE_SIZE });
    } else {
      setMessages([]);
    }
  }, [room?.session_key, triggerMessages]);

  // Update messages when data arrives
  useEffect(() => {
    if (messagesData?.messages) {
      console.log(`📦 Received messages data:`, {
        page: messagesData.page,
        totalPages: messagesData.totalPages,
        messageCount: messagesData.messages.length,
        hasMore: messagesData.page < messagesData.totalPages
      });

      // Transform server data to MessageData format
      const transformedMessages = messagesData.messages.map((m: any) => {
        // Determine sender name based on user_type (1 = client, 3 = agent)
        const senderName = m?.user_type === 1
          ? m?.sender?.client_username || ""
          : m?.sender?.agent_username || "";

        // For replied messages, determine sender name based on user_type
        const repliedToSenderName = m.replied_to?.user_type === 1
          ? m.replied_to?.sender?.client_username || ""
          : m.replied_to?.sender?.agent_username || "";

        return {
          messageId: m.message_id,
          text: m.message || m.message_text || "",
          isOwner: m.isOwner || false,
          senderName,
          sentTime: m.message_created_at,
          deliveredTime: null,
          arrivalTime: m.message_created_at,
          seenTimes: [],
          editedTime: m.message_edited_at || null,
          deletedTime: m.message_deleted_at || null,
          contentType: m.content_type || "text/plain",
          messageType: m.media_url ? "file-transfer" : undefined,
          fileName: m.file_name || undefined,
          filePath: m.media_url || undefined,
          fileType: m.content_type || undefined,
          media_url: m.media_url || undefined,
          message_json: m.message_json || null,
          repliedTo: m.replied_to ? {
            messageId: m.replied_to.message_id,
            text: m.replied_to.message || "",
            senderName: repliedToSenderName,
            isDeleted: m.replied_to.is_deleted || false,
            isFileMessage: !!m.replied_to.media_url,
            fileName: m.replied_to.file_name || null,
            contentType: m.replied_to.content_type || "text/plain",
          } : null,
          sender: m.sender || null,
        };
      });

      // If page > 1, prepend to existing messages (pagination)
      // Otherwise replace (new room or refresh)
      // Backend sends messages in correct order (oldest to newest)
      if (messagesData.page && messagesData.page > 1) {
        console.log(`📝 Prepending ${transformedMessages.length} messages from page ${messagesData.page}`);
        setMessages((prev) => {
          const newMessages = [...transformedMessages, ...prev];
          console.log(`✅ Total messages after prepend: ${newMessages.length} (was ${prev.length})`);
          return newMessages;
        });
      } else {
        console.log(`📝 Loading ${transformedMessages.length} messages (page 1)`);
        setMessages(transformedMessages);
      }
    }
  }, [messagesData]);

  // Listen to WebSocket events and trigger REST API to fetch latest messages
  // TODO: In the future, process WebSocket data directly instead of making REST API requests
  useEffect(() => {
    if (!wrapper?.endpoint || !room?.session_key) return;
    console.log("✅ WebSocket event listeners attached for room:", room.session_key);

    // Handler for new messages (including files)
    const handleNewMessage = (_msg: string, messageId: string, sender: string, receiver: string, type: string) => {
      console.log("📨 WebSocket onMsg event received:", { messageId, sender, receiver, type, msg: _msg });

      // Check if message already exists to avoid duplicates
      const messageExists = messages.some(m => m.messageId === messageId);

      if (!messageExists) {
        console.log("📡 Message not found in local array, fetching from REST API...");

        // Show notification for new message
        const senderName = sender.split('@')[0]; // Extract username before @
        const messagePreview = _msg.length > 50 ? _msg.substring(0, 50) + "..." : _msg;
        showNotification(`New message from ${senderName}`, messagePreview);

        triggerMessages({ sessionKey: room.session_key, page: 1, limit: PAGE_SIZE });
      } else {
        console.log("✅ Message already exists, skipping REST API call");
      }
    };

    // Handler for urgent messages
    const handleUrgentMessage = (_msg: string, messageId: string, sender: string, receiver: string, type: string) => {
      console.log("🚨 WebSocket urgent message received, fetching from REST API...", { messageId, sender, receiver, type });

      const messageExists = messages.some(m => m.messageId === messageId);

      if (!messageExists) {
        console.log("📡 Urgent message not found in local array, fetching from REST API...");

        // Show notification for urgent message
        const senderName = sender.split('@')[0];
        const messagePreview = _msg.length > 50 ? _msg.substring(0, 50) + "..." : _msg;
        showNotification(`🚨 Urgent from ${senderName}`, messagePreview);

        triggerMessages({ sessionKey: room.session_key, page: 1, limit: PAGE_SIZE });
      } else {
        console.log("✅ Urgent message already exists, skipping REST API call");
      }
    };

    // Handler for reply messages (different signature!)
    const handleReplyMessage = (_msg: string, messageId: string, repliedMessageId: string, sender: string, receiver: string, type: string) => {
      console.log("💬 WebSocket reply message received, fetching from REST API...", { messageId, repliedMessageId, sender, receiver, type });

      const messageExists = messages.some(m => m.messageId === messageId);

      if (!messageExists) {
        console.log("📡 Reply message not found in local array, fetching from REST API...");

        // Show notification for reply
        const senderName = sender.split('@')[0];
        const messagePreview = _msg.length > 50 ? _msg.substring(0, 50) + "..." : _msg;
        showNotification(`💬 ${senderName} replied`, messagePreview);

        triggerMessages({ sessionKey: room.session_key, page: 1, limit: PAGE_SIZE });
      } else {
        console.log("✅ Reply message already exists, skipping REST API call");
      }
    };

    // Handler for edit messages
    const handleEditMessage = (_msg: string, currentMessageId: string, sender: string, receiver: string, type: string) => {
      console.log("✏️ WebSocket edit message received, fetching from REST API...", { currentMessageId, sender, receiver, type });

      // For edits, always fetch to get the updated content
      console.log("📡 Fetching from REST API to get updated message...");
      triggerMessages({ sessionKey: room.session_key, page: 1, limit: PAGE_SIZE });
    };

    // Handler for control messages (handles delete, file transfer, etc.)
    const handleControlMessage = (msg: any) => {
      console.log("🎛️ WebSocket control message received:");
      console.log("  - Full msg object:", JSON.stringify(msg, null, 2));
      console.log("  - msg.job:", msg.job);
      console.log("  - msg.type:", msg.type);
      console.log("  - msg.cm:", msg.cm);
      console.log("  - msg.parameters:", msg.parameters);

      // Check if it's a delete-message request/response/notification
      if (msg.job && (msg.job === "delete-message" || JSON.stringify(msg.job).includes("delete"))) {
        console.log("🗑️ Delete message detected, fetching from REST API...");

        // Show notification for deleted message
        const fromName = msg.cm?.fromName || msg.parameters?.["from-name"] || "Someone";
        const senderName = fromName.split('@')[0];
        showNotification(`🗑️ ${senderName} deleted a message`, "A message was deleted");

        triggerMessages({ sessionKey: room.session_key, page: 1, limit: PAGE_SIZE });
      }
      // Check if it's a file-transfer request/response/notification
      else if (msg.job && (msg.job === "file-transfer" || JSON.stringify(msg.job).includes("file"))) {
        console.log("📎 File transfer detected, fetching from REST API...");

        // Extract message ID from the control message if available
        const messageId = msg.cm?.messageId || msg.parameters?.["message-id"];
        const messageExists = messageId ? messages.some(m => m.messageId === messageId) : false;

        if (!messageExists || !messageId) {
          // Show notification for file transfer
          const fromName = msg.cm?.fromName || msg.parameters?.["from-name"] || "Someone";
          const fileName = msg.cm?.fileName || msg.parameters?.["file-name"] || "a file";
          const senderName = fromName.split('@')[0];
          showNotification(`📎 ${senderName} sent a file`, fileName);

          triggerMessages({ sessionKey: room.session_key, page: 1, limit: PAGE_SIZE });
        } else {
          console.log("✅ File transfer message already exists, skipping REST API call");
        }
      }
      // Check if it's a start-conference request/response/notification
      else if (msg.job && (msg.job === "start-conference" || msg.job?.str === "start-conference" || JSON.stringify(msg.job).includes("start-conference"))) {
        console.log("🎉 Conference start detected");

        // Extract conference details
        const conferenceId = msg.cm?.conferenceId || msg.parameters?.["conference-id"] || "New Conference";
        console.log("Conference ID:", conferenceId);

        // Refresh the chat rooms list to show the new conference
        if (onRefreshRooms) {
          console.log("📡 Refreshing rooms list to show new conference...");
          onRefreshRooms();
        }

        // Auto-select the new conference after a short delay to allow rooms to refresh
        if (onAutoSelectRoom && conferenceId) {
          setTimeout(() => {
            console.log("🎯 Auto-selecting conference:", conferenceId);
            onAutoSelectRoom(conferenceId);
          }, 500);
        }

        console.log("✅ Conference start handled - rooms list refreshed");
      }
      // Check if it's an invite-member (request, response, or notification)
      else if (msg.job && (msg.job === "invite-member" || JSON.stringify(msg.job).includes("invite-member"))) {
        const messageType = msg.type || "unknown";
        console.log(`🎉 Invitation ${messageType} - conference invitation event`);

        // Extract the room/conference ID from the message
        const roomId = msg.cm?.to || msg.parameters?.["chatroom-id"] || msg.parameters?.["to-name"] || msg.cm?.fromName;
        console.log("Room ID from invitation:", roomId);
        console.log("Message type:", messageType);

        // Get current user info
        const toName = msg.cm?.to || msg.parameters?.["to-name"];
        const fromName = msg.cm?.fromName || msg.parameters?.["from-name"];
        const currentUserFullName = Global.username + Global.companyDomain;

        // Refresh rooms for:
        // 1. REQUEST: When invitation is sent TO current user (invitation received)
        // 2. RESPONSE: When invitation is accepted BY current user (acceptance sent)
        // 3. NOTIFICATION: When someone joins the conference (notification received)
        const shouldRefresh =
          (messageType === "request" && toName === currentUserFullName) || // I received an invitation
          (messageType === "response" && fromName === currentUserFullName) || // I accepted an invitation
          (messageType === "notification"); // Someone joined

        if (shouldRefresh) {
          console.log(`🎯 Refreshing rooms for ${messageType} (current user: ${currentUserFullName})`);

          // Refresh the chat rooms list
          if (onRefreshRooms) {
            console.log("📡 Refreshing rooms list after invitation event...");
            onRefreshRooms();
          }

          // Auto-select the room after a short delay to allow rooms to refresh
          // Only auto-select for response (accepted) and notification (joined)
          if (onAutoSelectRoom && roomId && (messageType === "response" || messageType === "notification")) {
            setTimeout(() => {
              console.log("🎯 Auto-selecting room after invitation:", roomId);
              onAutoSelectRoom(roomId);
            }, 800);
          }

          console.log(`✅ Invitation ${messageType} handled - rooms list refreshed`);
        } else {
          console.log(`ℹ️ Invitation ${messageType} for someone else, not refreshing`);
        }
      } else {
        console.log("⚠️ Unhandled control message job:", msg.job);
      }
    };

    // Handler for message delivery status (might include file messages)
    const handleDelivered = (messageID: string, deliveredTime: number) => {
      console.log("✅ WebSocket Delivered event:", { messageID, deliveredTime });

      // Check if this message exists in our array
      const messageExists = messages.some(m => m.messageId === messageID);
      if (!messageExists) {
        console.log("📡 Delivered message not found locally, fetching from REST API...");
        triggerMessages({ sessionKey: room.session_key, page: 1, limit: PAGE_SIZE });
      }
    };

    const handleSent = (messageID: string, sentTime: number) => {
      console.log("✅ WebSocket Sent event:", { messageID, sentTime });

      // Check if this message exists in our array
      const messageExists = messages.some(m => m.messageId === messageID);
      if (!messageExists) {
        console.log("📡 Sent message not found locally, fetching from REST API...");
        triggerMessages({ sessionKey: room.session_key, page: 1, limit: PAGE_SIZE });
      }
    };

    // Attach event listeners
    wrapper.endpoint.on("onMsg", handleNewMessage);
    wrapper.endpoint.on("onUrgentMsg", handleUrgentMessage);
    wrapper.endpoint.on("onReply", handleReplyMessage);
    wrapper.endpoint.on("onEdit", handleEditMessage);
    wrapper.endpoint.on("ReqCmsg", handleControlMessage);
    wrapper.endpoint.on("RespCmsg", handleControlMessage);
    wrapper.endpoint.on("NotfCmsg", handleControlMessage);
    wrapper.endpoint.on("Delivered", handleDelivered);
    wrapper.endpoint.on("Sent", handleSent);

    // Cleanup listeners on unmount
    return () => {
      wrapper.endpoint.off("onMsg", handleNewMessage);
      wrapper.endpoint.off("onUrgentMsg", handleUrgentMessage);
      wrapper.endpoint.off("onReply", handleReplyMessage);
      wrapper.endpoint.off("onEdit", handleEditMessage);
      wrapper.endpoint.off("ReqCmsg", handleControlMessage);
      wrapper.endpoint.off("RespCmsg", handleControlMessage);
      wrapper.endpoint.off("NotfCmsg", handleControlMessage);
      wrapper.endpoint.off("Delivered", handleDelivered);
      wrapper.endpoint.off("Sent", handleSent);
    };
  }, [wrapper, room?.session_key, messages, triggerMessages]);

  // Message operations handlers
  const handleEdit = (messageId: string, newText: string) => {
    if (onEditMessage) {
      onEditMessage(messageId, newText);
    }
    // Update the message locally without refetching
    setMessages((prev) =>
      prev.map((m) =>
        m.messageId === messageId
          ? { ...m, text: newText, editedTime: new Date().toISOString() }
          : m
      )
    );
  };

  const handleDelete = (messageId: string, agentUsername?: string) => {
    if (onDeleteMessage) {
      onDeleteMessage(messageId, agentUsername);
    }
    // Update the message locally without refetching
    setMessages((prev) =>
      prev.map((m) =>
        m.messageId === messageId
          ? { ...m, deletedTime: new Date().toISOString() }
          : m
      )
    );
  };

  const handleReply = (message: MessageData) => {
    setRepliedMessage(message);
  };

  const messageHandlers = useMessageOperations(handleEdit, handleDelete, handleReply);

  // Load more messages
  const handleLoadMore = () => {
    if (room?.session_key && !messagesLoading && messagesData?.page && messagesData.page < messagesData.totalPages) {
      const nextPage = messagesData.page + 1;
      console.log(`📄 Loading page ${nextPage} of ${messagesData.totalPages} (current: ${messagesData.page})`);
      triggerMessages({ sessionKey: room.session_key, page: nextPage, limit: PAGE_SIZE });
    } else {
      console.log(`❌ Cannot load more:`, {
        hasRoom: !!room?.session_key,
        isLoading: messagesLoading,
        currentPage: messagesData?.page,
        totalPages: messagesData?.totalPages,
        hasMore: messagesData?.page && messagesData.page < messagesData.totalPages
      });
    }
  };

  // Handle sending text messages
  const handleSendTextMessage = (text: string, sender: string, isOwner: boolean, id: string) => {
    if (onSendMessage) {
      onSendMessage(text, sender, isOwner, id);
    }
    // Add optimistic message to UI
    const nowIso = new Date().toISOString();
    const optimistic: any = {
      messageId: id,
      text,
      isOwner,
      senderName: sender,
      sentTime: nowIso,
      arrivalTime: nowIso,
      seenTimes: [],
      editedTime: null,
      deletedTime: null,
      contentType: "text/plain",
      repliedTo: repliedMessage || null,
    };
    setMessages((prev) => [...prev, optimistic]);
    setRepliedMessage(null);
  };

  // Handle sending file messages
  const handleSendFile = (fileMessage: any) => {
    if (onSendFileMessage) {
      onSendFileMessage(fileMessage);
    }
    // Add optimistic file message to UI
    setMessages((prev) => [...prev, fileMessage]);
  };

  // Header action handlers
  const handleGoBack = () => {
    // On small screens, this would hide the chat view and show the sidebar
    // This might need to be handled by the parent component
    console.log("Go back clicked");
  };

  const handleOpenDrawer = () => {
    setIsDrawerOpen(true);
  };

  const handleInvite = () => {
    // Fetch agents list before showing the modal
    updateAgentList("all-status");
    setShowInviteModal(true);
  };

  const handleCloseSession = () => {
    setIsCloseSessionModalOpen(true);
  };

  const handleLeave = () => {
    setLeaveConfirmationWindow(true);
  };

  const handleDestroy = () => {
    setDestroyConfirmationWindow(true);
  };

  // Modal handler functions
  const getFilteredAvailableAgents = () => {
    const availableAgents = agents || [];
    if (!room) return availableAgents;

    // Filter out current participants and current user
    const currentParticipants = Array.isArray(room.session_key?.split('+'))
      ? room.session_key.split('+')
      : [];

    const excludedUsernames = [
      ...currentParticipants,
      room.session_key,
      Global.username + Global.companyDomain
    ];

    return availableAgents.filter(agent =>
      !excludedUsernames.includes(agent.name)
    );
  };

  const handleAgentSelection = (agent: Agent) => {
    setSelectedAgents((prev) =>
      prev.find(a => a.id === agent.id)
        ? prev.filter((a) => a.id !== agent.id)
        : [...prev, agent]
    );
  };

  const handleAgentInviteSubmit = () => {
    if (selectedAgents.length === 0) {
      toast.warning("Please select at least one agent.");
      return;
    }

    setShowInviteModal(false);

    // Check if we need to create a conference first
    if (room?.session_type !== "conference" && room?.session_type !== "group-chat") {
      setShowConferenceNameInput(true);
    } else if (room?.session_type === "conference") {
      // Send invite for existing conference
      const agentNames = selectedAgents.map((agent) => agent.name);
      const confName = room.session_key;

      agentNames.forEach((agentName) => {
        const reqId = generateRandomString(16);
        const params = {
          id: reqId,
          type: "request",
          job: "invite-member",
          parameters: {
            "to-name": agentName,
            "from-name": Global.username + Global.companyDomain,
            "chatroom-id": confName,
            "chat-type": "conference",
          },
        };
        const jsonMessage = JSON.stringify(params, null, 4);
        wrapper?.sendMsg(jsonMessage, "application/rechat.cntl+json");
      });

      setSelectedAgents([]);
      toast.success("Invite sent successfully");
    } else if (room?.session_type === "group-chat") {
      // Send invite for existing group
      const agentNames = selectedAgents.map((agent) => agent.name);
      const groupName = room.session_key;

      agentNames.forEach((agentName) => {
        const reqID = generateRandomString(16);
        const params = {
          id: reqID,
          type: "request",
          job: "added-participant",
          parameters: {
            "from-name": Global.username + Global.companyDomain,
            "to-name": agentName,
            "chat-id": groupName,
            "chat-type": "group-chat",
          },
        };
        const jsonMessage = JSON.stringify(params, null, 4);
        wrapper?.sendMsg(jsonMessage, "application/rechat.cntl+json");
      });

      setSelectedAgents([]);
      toast.success("Participant added successfully");
    }
  };

  const handleConferenceNameSubmit = () => {
    if (conferenceName.trim() === "") {
      toast.warning("Please enter a conference name.");
      return;
    }

    const agentNames = Array.from(
      new Set(selectedAgents.map((agent) => agent.name))
    );

    const params = {
      "from-name": Global.username + Global.companyDomain,
      "conference-id": conferenceName.trim(),
      "client-name": room?.session_key?.substring(room.session_key.indexOf("+") + 1) ?? "",
      "user-list": agentNames,
    };

    const messageId = generateRandomString(16);
    const messageJson = {
      id: messageId,
      type: "request",
      job: "start-conference",
      parameters: params,
    };

    wrapper?.sendMsg(
      JSON.stringify(messageJson, null, 4),
      "application/rechat.cntl+json"
    );

    setSelectedAgents([]);
    setConferenceName("");
    setShowConferenceNameInput(false);
    toast.success("Conference creation request sent");
  };

  const submitCloseSession = () => {
    if (!closeSessionReason.trim()) {
      toast.warning("Please provide a reason to close the session.");
      return;
    }

    const messageId = generateRandomString(16);
    const messageJson = {
      id: messageId,
      type: "request",
      job: "session-close",
      parameters: {
        "from-name": Global.username + Global.companyDomain,
        "target-name": room?.session_key,
        "reason": closeSessionReason,
      },
    };

    wrapper?.sendMsg(
      JSON.stringify(messageJson, null, 4),
      "application/rechat.cntl+json"
    );

    setIsCloseSessionModalOpen(false);
    setCloseSessionReason("");
    toast.success("Session close request sent");
  };

  const confirmLeaveChat = () => {
    const reqID = generateRandomString(16);
    const messageJson = {
      id: reqID,
      type: "request",
      job: "leave-chat",
      parameters: {
        name: Global.username + Global.companyDomain,
        "chat-id": room?.session_key,
        "chat-type": room?.session_type,
      },
    };

    wrapper?.sendMsg(JSON.stringify(messageJson, null, 4), "application/rechat.cntl+json");
    setLeaveConfirmationWindow(false);
    toast.success("Leave request sent");

    // Refresh rooms list and clear selection
    if (onRefreshRooms) {
      onRefreshRooms();
    }
    if (onClearSelection) {
      onClearSelection();
    }
  };

  const confirmDestroySession = () => {
    const chatId = room?.session_key || "";
    const reqID = generateRandomString(16);
    const messageJson = {
      id: reqID,
      type: "request",
      job: "delete-chat",
      parameters: {
        "chat-id": chatId,
        "chat-type": room?.session_type,
      },
    };

    wrapper?.sendMsg(JSON.stringify(messageJson, null, 4), "application/rechat.cntl+json");
    setDestroyConfirmationWindow(false);
    toast.success("Destroy request sent");

    // Refresh rooms list and clear selection
    if (onRefreshRooms) {
      onRefreshRooms();
    }
    if (onClearSelection) {
      onClearSelection();
    }
  };

  // Show empty state if no room selected
  if (!room) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-50 dark:bg-gray-900">
        <div className="text-center p-8">
          <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-blue-100 to-purple-100 dark:from-blue-900/30 dark:to-purple-900/30 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            No conversation selected
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Choose a conversation from the sidebar to start chatting
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full w-full bg-white dark:bg-gray-900">
      {/* Header */}
      <ChatHeader
        currentRoom={room}
        isSmallScreen={isSmallScreen}
        onGoBack={handleGoBack}
        onOpenDrawer={handleOpenDrawer}
        onInvite={handleInvite}
        onCloseSession={handleCloseSession}
        onLeave={handleLeave}
        onDestroy={handleDestroy}
      />

      {/* Messages - takes remaining space */}
      <div className="flex-1 min-h-0 overflow-hidden">
        <MessageList
          messages={messages}
          currentUser={currentUser}
          isLoading={messagesLoading}
          onLoadMore={() => {
            console.log(`📊 LoadMore called. Page: ${messagesData?.page}/${messagesData?.totalPages}, hasMore: ${messagesData?.page ? messagesData.page < messagesData.totalPages : false}`);
            handleLoadMore();
          }}
          hasMore={messagesData?.page ? messagesData.page < messagesData.totalPages : false}
          eventHandlers={messageHandlers}
          repliedMessage={repliedMessage}
          currentRoom={room}
        />
      </div>

      {/* Message Input - in normal flow (not fixed) */}
      <div className="flex-shrink-0">
        <MessageInput
          wrapper={wrapper}
          currentRoom={room as any}
          chatroomusername={
            room.session_type === "c2a"
              ? room.client?.client_username || ""
              : room.session_type === "agent-to-agent"
              ? (() => {
                  // For agent-to-agent, find the receiver from participants where is_current_user is false
                  const receiver = room.participants?.find(p => p.is_current_user === false);
                  return receiver?.user_type === 3 ? (receiver.details as any).agent_username || "" : "";
                })()
              : room.agent?.agent_username || ""
          }
          roomType={room.session_type || ""}
          repliedMessage={repliedMessage}
          setRepliedMessage={setRepliedMessage}
          onSendMessage={handleSendTextMessage}
          onSendFileMessage={handleSendFile}
          isFixed={false}
        />
      </div>

      {/* Chat Drawer */}
      <ChatBarDrawer currentRoom={room} open={isDrawerOpen} onClose={() => setIsDrawerOpen(false)} />

      {/* Close Session Modal */}
      <Modal
        onClose={() => setIsCloseSessionModalOpen(false)}
        popup
        size="lg"
        show={isCloseSessionModalOpen}
      >
        <Modal.Body className="relative p-0 bg-white rounded-lg shadow dark:bg-gray-800">
          <div className="flex items-center justify-between p-4 sm:px-5">
            <h3 className="text-lg font-semibold leading-none text-gray-900 dark:text-white">
              Session Close
            </h3>
            <button
              onClick={() => setIsCloseSessionModalOpen(false)}
              className="absolute right-2.5 top-2.5 ml-auto inline-flex items-center rounded-lg bg-transparent p-1.5 text-sm text-gray-400 hover:bg-gray-200 hover:text-gray-900 dark:hover:bg-gray-600 dark:hover:text-white"
            >
              <svg
                aria-hidden
                fill="currentColor"
                viewBox="0 0 20 20"
                xmlns="http://www.w3.org/2000/svg"
                className="w-5 h-5"
              >
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
              <span className="sr-only">Close modal</span>
            </button>
          </div>
          <div className="p-4 sm:p-5">
            <form onSubmit={(e) => { e.preventDefault(); submitCloseSession(); }}>
              <div className="mt-2 mb-4">
                <Label
                  htmlFor="reason"
                  className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                >
                  Enter the reason to close the session:
                </Label>
                <TextInput
                  value={closeSessionReason}
                  id="reason"
                  onChange={(e) => setCloseSessionReason(e.target.value)}
                  placeholder="Reason for closing"
                  required
                />
              </div>
              <div className="flex items-center justify-center space-x-4">
                <Button
                  color="gray"
                  onClick={() => setIsCloseSessionModalOpen(false)}
                >
                  Cancel
                </Button>
                <Button color="purple" type="submit">
                  Submit
                </Button>
              </div>
            </form>
          </div>
        </Modal.Body>
      </Modal>

      {/* Invite Agents Modal */}
      <Modal
        onClose={() => {
          setShowInviteModal(false);
          setSelectedAgents([]);
        }}
        popup
        show={showInviteModal}
        size="xl"
      >
        <Modal.Header>Please select Agents to Invite:</Modal.Header>
        <Modal.Body>
          {getFilteredAvailableAgents().length === 0 ? (
            <legend className="mb-4 text-gray-600 dark:text-gray-400">
              No available agents to invite at the moment.
            </legend>
          ) : (
            <fieldset className="flex flex-col max-w-md gap-4 my-6">
              {getFilteredAvailableAgents().map((agent) => (
                <div className="flex items-center gap-2" key={agent.id}>
                  <Checkbox
                    id={String(agent.id)}
                    name={agent.name}
                    value={agent.name}
                    checked={selectedAgents.some(a => a.id === agent.id)}
                    onChange={() => handleAgentSelection(agent)}
                  />
                  <Label htmlFor={String(agent.id)}>{agent.name.split("@")[0]}</Label>
                </div>
              ))}
            </fieldset>
          )}
          <div className="flex items-center justify-center space-x-4">
            {getFilteredAvailableAgents().length > 0 && (
              <Button color="purple" onClick={handleAgentInviteSubmit}>
                Submit
              </Button>
            )}
            <Button
              color="gray"
              onClick={() => {
                setShowInviteModal(false);
                setSelectedAgents([]);
              }}
            >
              Cancel
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      {/* Conference Name Modal */}
      <Modal
        onClose={() => setShowConferenceNameInput(false)}
        popup
        size="lg"
        show={showConferenceNameInput}
      >
        <Modal.Body className="relative p-0 bg-white rounded-lg shadow dark:bg-gray-800">
          <div className="flex items-center justify-between p-4 sm:px-5">
            <h3 className="text-lg font-semibold leading-none text-gray-900 dark:text-white">
              Conference Name
            </h3>
            <button
              onClick={() => setShowConferenceNameInput(false)}
              className="absolute right-2.5 top-2.5 ml-auto inline-flex items-center rounded-lg bg-transparent p-1.5 text-sm text-gray-400 hover:bg-gray-200 hover:text-gray-900 dark:hover:bg-gray-600 dark:hover:text-white"
            >
              <svg
                aria-hidden
                fill="currentColor"
                viewBox="0 0 20 20"
                xmlns="http://www.w3.org/2000/svg"
                className="w-5 h-5"
              >
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
              <span className="sr-only">Close modal</span>
            </button>
          </div>
          <div className="p-4 sm:p-5">
            <form onSubmit={(e) => { e.preventDefault(); handleConferenceNameSubmit(); }}>
              <div className="mt-2 mb-4">
                <Label
                  htmlFor="conference-name"
                  className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                >
                  Enter Conference Name:
                </Label>
                <TextInput
                  value={conferenceName}
                  onChange={(e) => setConferenceName(e.target.value)}
                  placeholder="Conference Name"
                  id="conference-name"
                  required
                />
              </div>
              <div className="flex items-center justify-center space-x-4">
                <Button
                  color="gray"
                  onClick={() => setShowConferenceNameInput(false)}
                >
                  Cancel
                </Button>
                <Button color="purple" type="submit">
                  Submit
                </Button>
              </div>
            </form>
          </div>
        </Modal.Body>
      </Modal>

      {/* Leave Chat Confirmation Modal */}
      <Modal
        onClose={() => setLeaveConfirmationWindow(false)}
        popup
        size="md"
        show={leaveConfirmationWindow}
      >
        <Modal.Body className="relative p-4 text-center bg-white rounded-lg shadow dark:bg-gray-800 sm:p-5">
          <button
            onClick={() => setLeaveConfirmationWindow(false)}
            className="absolute right-2.5 top-2.5 ml-auto inline-flex items-center rounded-lg bg-transparent p-1.5 text-sm text-gray-400 hover:bg-gray-200 hover:text-gray-900 dark:hover:bg-gray-600 dark:hover:text-white"
          >
            <svg
              aria-hidden
              fill="currentColor"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
              className="w-5 h-5"
            >
              <path
                fillRule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
            <span className="sr-only">Close modal</span>
          </button>
          <svg
            aria-hidden
            fill="currentColor"
            viewBox="0 0 20 20"
            xmlns="http://www.w3.org/2000/svg"
            className="mx-auto mb-3.5 h-11 w-11 text-gray-400 dark:text-gray-500"
          >
            <path
              fillRule="evenodd"
              d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
          <p className="mb-4 text-gray-500 dark:text-gray-300">
            Are you sure you want to leave session?
          </p>
          <div className="flex items-center justify-center space-x-4">
            <Button
              color="gray"
              onClick={() => setLeaveConfirmationWindow(false)}
            >
              Cancel
            </Button>
            <Button color="purple" onClick={confirmLeaveChat}>
              Leave
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      {/* Destroy Session Confirmation Modal */}
      <Modal
        onClose={() => setDestroyConfirmationWindow(false)}
        popup
        size="md"
        show={destroyConfirmationWindow}
      >
        <Modal.Body className="relative p-4 text-center bg-white rounded-lg shadow dark:bg-gray-800 sm:p-5">
          <button
            onClick={() => setDestroyConfirmationWindow(false)}
            className="absolute right-2.5 top-2.5 ml-auto inline-flex items-center rounded-lg bg-transparent p-1.5 text-sm text-gray-400 hover:bg-gray-200 hover:text-gray-900 dark:hover:bg-gray-600 dark:hover:text-white"
          >
            <svg
              aria-hidden
              fill="currentColor"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
              className="w-5 h-5"
            >
              <path
                fillRule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
            <span className="sr-only">Close modal</span>
          </button>
          <svg
            aria-hidden
            fill="currentColor"
            viewBox="0 0 20 20"
            xmlns="http://www.w3.org/2000/svg"
            className="mx-auto mb-3.5 h-11 w-11 text-gray-400 dark:text-gray-500"
          >
            <path
              fillRule="evenodd"
              d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
          <p className="mb-4 text-gray-500 dark:text-gray-300">
            Are you sure you want to close this chat?
          </p>
          <div className="flex items-center justify-center space-x-4">
            <Button
              color="gray"
              onClick={() => setDestroyConfirmationWindow(false)}
            >
              Cancel
            </Button>
            <Button color="purple" onClick={confirmDestroySession}>
              Close
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default ChatAreaChild;
